import get from 'lodash/get';
import axiosConfig from '../axiosConfig';

const unwrapData = (response, fallback = null) => get(response, ['data', 'data'], get(response, ['data'], fallback));

const unwrapList = (response, key) => {
  const data = unwrapData(response, []);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  return [];
};

const withOptionalParams = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') acc[key] = value;
    return acc;
  }, {});

export const pipeMillReportApi = {
  fetchReports: async (params = {}) => {
    const response = await axiosConfig.get('/pipeMaterialReports/getPipeMaterialReports', {
      params: withOptionalParams(params),
    });
    return unwrapList(response, 'reports');
  },

  fetchMockData: async (params = {}) => {
    const response = await axiosConfig.get('/pipeMillReports/getPipeMillMockData', {
      params: withOptionalParams(params),
    });
    return unwrapData(response, {});
  },

  fetchMetadata: async ({ reportType, projectId, pipeId, heatNumber, pipeNumber, itemNumber } = {}) => {
    const response = await axiosConfig.get('/pipeMaterialReports/getPipeMillReportMetadata', {
      params: withOptionalParams({ reportType, projectId, pipeId, heatNumber, pipeNumber, itemNumber }),
    });
    const data = unwrapData(response, null);
    if (Array.isArray(data?.metadata)) return data.metadata;
    return data;
  },

  fetchHeatLotView: async (params = {}) => {
    const response = await axiosConfig.get('/pipeMaterialReports/getHeatLotView', {
      params: withOptionalParams(params),
    });
    return unwrapData(response, { items: [], heatGroups: [], summary: null });
  },

  saveReport: async (payload) => {
    const response = await axiosConfig.post('/pipeMaterialReports/savePipeMillReportMetadata', payload);
    return unwrapData(response, null);
  },

  saveBulkReports: async (payload) => {
    const response = await axiosConfig.post('/pipeMaterialReports/saveBulkPipeMillReports', payload);
    return unwrapData(response, null);
  },

  saveReportItem: async (payload) => {
    const response = await axiosConfig.post('/pipeMaterialReports/savePipeMaterialReportItem', payload);
    return unwrapData(response, null);
  },

  saveReportStatus: async (payload) => {
    const response = await axiosConfig.post('/pipeMaterialReports/savePipeMaterialReportStatus', payload);
    return unwrapData(response, null);
  },

  deleteReport: async ({ reportId, id } = {}) => {
    const deleteId = reportId || id;
    const response = await axiosConfig.delete(`/pipeMaterialReports/deletePipeMaterialReport/${deleteId}`);
    return unwrapData(response, { id: deleteId, reportId: deleteId });
  },

  deleteReportItem: async ({ reportId, itemId, item_id } = {}) => {
    const deleteItemId = itemId || item_id;
    const response = await axiosConfig.delete(`/pipeMaterialReports/deletePipeMaterialReportItem/${reportId}/${deleteItemId}`);
    return unwrapData(response, null);
  },

  evaluateReport: async ({ reportId } = {}) => {
    const response = await axiosConfig.post('/pipeMillReports/evaluatePipeMillReport', { reportId });
    return unwrapData(response, null);
  },

  fetchProjectLimits: async (params = {}) => {
    const response = await axiosConfig.get('/projectLimits/getProjectLimitsList', {
      params: withOptionalParams(params),
    });
    return unwrapList(response, 'limits');
  },

  saveProjectLimit: async (payload) => {
    const response = await axiosConfig.post('/projectLimits/saveProjectLimit', payload);
    return unwrapData(response, null);
  },

  resetDefaultProjectLimits: async (payload) => {
    const response = await axiosConfig.post('/projectLimits/resetDefaultProjectLimits', payload);
    return unwrapData(response, null);
  },

  deleteProjectLimit: async ({ limitId, id } = {}) => {
    const deleteId = limitId || id;
    const response = await axiosConfig.delete(`/projectLimits/deleteProjectLimit/${deleteId}`);
    return unwrapData(response, { id: deleteId, limitId: deleteId });
  },

  fetchFrequencyRules: async (params = {}) => {
    const response = await axiosConfig.get('/projectLimits/getFrequencyRules', {
      params: withOptionalParams(params),
    });
    return unwrapList(response, 'frequencyRules');
  },

  fetchPipeDashboard: async (params = {}) => {
    const response = await axiosConfig.get('/projectLimits/getPipeDashboard', {
      params: withOptionalParams(params),
    });
    return unwrapData(response, { items: [], summary: null });
  },

  saveFrequencyRule: async (payload) => {
    const response = await axiosConfig.post('/projectLimits/saveFrequencyRule', payload);
    return unwrapData(response, null);
  },

  resetDefaultFrequencyRules: async (payload) => {
    const response = await axiosConfig.post('/projectLimits/resetDefaultFrequencyRules', payload);
    return unwrapData(response, null);
  },

  deleteFrequencyRule: async ({ ruleId, id } = {}) => {
    const deleteId = ruleId || id;
    const response = await axiosConfig.delete(`/projectLimits/deleteFrequencyRule/${deleteId}`);
    return unwrapData(response, { id: deleteId, ruleId: deleteId });
  },

  fetchClearanceStatus: async ({ projectId, pipeId } = {}) => {
    const response = await axiosConfig.get(`/pipeMillReports/getPipeMillClearanceStatus/${projectId}/${pipeId}`);
    return unwrapData(response, null);
  },
};
